export const UNASSIGNED_COMMAND_ID = '__unassigned__';

export type ExternalCallTreeSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';
export type RequestTreeKind = 'command' | 'method' | 'http';

export interface ExternalCallTreeLeaf {
  id: string;
  serviceName: string;
  httpMethod: string;
  url: string;
  urlLabel: string;
  statusLabel: string;
  severity: ExternalCallTreeSeverity;
  durationMs: number;
  proxyLabel: string | null;
  error: string | null;
  commandExecutionId: string | null;
}

export interface RequestTreeCommandSource {
  id: string;
  title: string;
  state: string;
  severity: ExternalCallTreeSeverity;
  logs: string[];
  inputJson: string;
  resultJson: string;
  attempt?: number | string;
  startedAt?: string | Date;
  duration?: string;
  workerId?: string;
  commandTypeShort?: string;
  error?: string | null;
  parentCommandId?: string | null;
}

export interface RequestTreeNode {
  id: string;
  kind: RequestTreeKind;
  title: string;
  isUnassigned: boolean;
  hasCalls: boolean;
  isExternal: boolean;
  expanded: boolean;
  state: string;
  severity: ExternalCallTreeSeverity;
  statusLabel: string | null;
  durationLabel: string | null;
  proxyLabel: string | null;
  callId: string | null;
  url: string | null;
  httpMethod: string | null;
  logs: string[];
  inputJson: string;
  resultJson: string;
  attempt: number | string;
  startedAt?: string | Date;
  duration: string;
  workerId: string;
  commandTypeShort: string;
  error: string | null;
  children: RequestTreeNode[];
}

export function shortExternalCallUrl(url: string): string {
  if (!url || url === '—') {
    return url || '—';
  }
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return stripUrlQuery(url);
  }
}

export function externalCallUrlWithoutQuery(url: string): string {
  if (!url || url === '—') {
    return url || '—';
  }
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return stripUrlQuery(url);
  }
}

export function externalCallRequestPath(url: string): string {
  if (!url || url === '—') {
    return url || '—';
  }
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
  } catch {
    return url;
  }
}

function stripUrlQuery(url: string): string {
  const queryStart = url.indexOf('?');
  const hashStart = url.indexOf('#');
  let end = url.length;
  if (queryStart >= 0) {
    end = Math.min(end, queryStart);
  }
  if (hashStart >= 0) {
    end = Math.min(end, hashStart);
  }
  return url.slice(0, end);
}

export function formatHttpRequestLine(
  method?: string | null,
  url?: string | null,
): string | null {
  const verb = (method ?? '').trim();
  const target = (url ?? '').trim();
  if (!target || target === '—') {
    return verb || null;
  }
  return verb ? `${verb} ${target}` : target;
}

export function formatHttpQueryParametersJson(url?: string | null): string | null {
  const target = (url ?? '').trim();
  if (!target || target === '—') {
    return null;
  }
  try {
    const parsed = target.includes('://')
      ? new URL(target)
      : new URL(target, 'https://placeholder.local');
    if (![...parsed.searchParams.keys()].length) {
      return null;
    }
    const parameters: Record<string, string | string[]> = {};
    for (const key of parsed.searchParams.keys()) {
      const values = parsed.searchParams.getAll(key);
      parameters[key] = values.length > 1 ? values : (values[0] ?? '');
    }
    return JSON.stringify(parameters, null, 2);
  } catch {
    return null;
  }
}

export function hasCapturedHttpBody(value?: string | null): boolean {
  const text = (value ?? '').trim();
  return !!text && text !== '[no body]' && !text.startsWith('[not stored:');
}

export function toggleTreeId(ids: Set<string>, id: string): void {
  if (ids.has(id)) {
    ids.delete(id);
    return;
  }
  ids.add(id);
}

export function findRequestTreeNode(nodes: RequestTreeNode[], id: string): RequestTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    const child = findRequestTreeNode(node.children, id);
    if (child) {
      return child;
    }
  }
  return null;
}

export function requestTreeHasId(nodes: RequestTreeNode[], id: string): boolean {
  return findRequestTreeNode(nodes, id) !== null;
}

export function applyRequestTreeExpand(
  nodes: RequestTreeNode[],
  expandedIds: ReadonlySet<string>,
): RequestTreeNode[] {
  return nodes.map(node => ({
    ...node,
    expanded: node.kind === 'http' ? false : expandedIds.has(node.id),
    children: applyRequestTreeExpand(node.children, expandedIds),
  }));
}

export function collectExpandableIds(nodes: RequestTreeNode[], into = new Set<string>()): Set<string> {
  for (const node of nodes) {
    if (node.kind === 'command' || node.kind === 'method') {
      into.add(node.id);
    }
    collectExpandableIds(node.children, into);
  }
  return into;
}

export function buildRequestStepTree(
  commands: RequestTreeCommandSource[],
  calls: ExternalCallTreeLeaf[],
  expandedIds: ReadonlySet<string> | null,
): RequestTreeNode[] {
  const callsByCommand = new Map<string, ExternalCallTreeLeaf[]>();
  const orphans: ExternalCallTreeLeaf[] = [];
  const commandIds = new Set(commands.map(command => command.id).filter(id => !!id));

  for (const call of calls) {
    const commandId = call.commandExecutionId;
    if (!commandId || !commandIds.has(commandId)) {
      orphans.push(call);
      continue;
    }
    const bucket = callsByCommand.get(commandId) ?? [];
    bucket.push(call);
    callsByCommand.set(commandId, bucket);
  }

  const nodeById = new Map<string, RequestTreeNode>();
  const commandById = new Map(commands.map(command => [command.id, command]));
  for (const command of commands) {
    nodeById.set(
      command.id,
      toCommandNode(command, callsByCommand.get(command.id) ?? [], false, expandedIds),
    );
  }

  const roots: RequestTreeNode[] = [];
  for (const command of commands) {
    const node = nodeById.get(command.id);
    if (!node) {
      continue;
    }
    const parentId = command.parentCommandId;
    const parent = parentId ? nodeById.get(parentId) : undefined;
    if (parent && parent.id !== node.id && !wouldCreateCommandCycle(commandById, parentId, command.id)) {
      insertChildCommand(parent, node);
      continue;
    }
    roots.push(node);
  }

  if (orphans.length) {
    roots.push(toCommandNode(
      {
        id: UNASSIGNED_COMMAND_ID,
        title: 'Unassigned command',
        state: '—',
        severity: 'secondary',
        logs: [],
        inputJson: '—',
        resultJson: '—',
        attempt: '—',
        duration: '—',
        workerId: '—',
        commandTypeShort: '',
        error: null,
      },
      orphans,
      true,
      expandedIds,
    ));
  }

  return roots;
}

function wouldCreateCommandCycle(
  commandsById: ReadonlyMap<string, RequestTreeCommandSource>,
  parentId: string | null | undefined,
  childId: string,
): boolean {
  let current = parentId ?? undefined;
  const seen = new Set<string>();
  while (current) {
    if (current === childId || seen.has(current)) {
      return true;
    }
    seen.add(current);
    current = commandsById.get(current)?.parentCommandId ?? undefined;
  }
  return false;
}

function insertChildCommand(parent: RequestTreeNode, child: RequestTreeNode): void {
  const methodStart = parent.children.findIndex(node => node.kind !== 'command');
  if (methodStart === -1) {
    parent.children.push(child);
  } else {
    parent.children.splice(methodStart, 0, child);
  }
  if (child.hasCalls || child.children.length > 0) {
    parent.hasCalls = true;
  }
}

function emptyCommandMeta(): Pick<
  RequestTreeNode,
  'attempt' | 'startedAt' | 'duration' | 'workerId' | 'commandTypeShort' | 'error'
> {
  return {
    attempt: '—',
    startedAt: undefined,
    duration: '—',
    workerId: '—',
    commandTypeShort: '',
    error: null,
  };
}

function toCommandNode(
  command: RequestTreeCommandSource,
  calls: ExternalCallTreeLeaf[],
  isUnassigned: boolean,
  expandedIds: ReadonlySet<string> | null,
): RequestTreeNode {
  const children: RequestTreeNode[] = [];
  const callsByService = new Map<string, ExternalCallTreeLeaf[]>();

  for (const call of calls) {
    const bucket = callsByService.get(call.serviceName) ?? [];
    bucket.push(call);
    callsByService.set(call.serviceName, bucket);
  }

  for (const [serviceName, serviceCalls] of callsByService) {
    const methodId = `${command.id}:method:${serviceName}`;
    children.push({
      id: methodId,
      kind: 'method',
      title: serviceName,
      isUnassigned: false,
      hasCalls: true,
      isExternal: false,
      expanded: expandedIds ? expandedIds.has(methodId) : true,
      state: '—',
      severity: 'info',
      statusLabel: null,
      durationLabel: null,
      proxyLabel: null,
      callId: null,
      url: null,
      httpMethod: null,
      logs: [],
      inputJson: '—',
      resultJson: '—',
      ...emptyCommandMeta(),
      children: serviceCalls.map(call => toHttpNode(call)),
    });
  }

  return {
    id: command.id,
    kind: 'command',
    title: command.title,
    isUnassigned,
    hasCalls: calls.length > 0,
    isExternal: false,
    expanded: expandedIds ? expandedIds.has(command.id) : true,
    state: command.state,
    severity: command.severity,
    statusLabel: null,
    durationLabel: null,
    proxyLabel: null,
    callId: null,
    url: null,
    httpMethod: null,
    logs: command.logs,
    inputJson: command.inputJson,
    resultJson: command.resultJson,
    attempt: command.attempt ?? '—',
    startedAt: command.startedAt,
    duration: command.duration ?? '—',
    workerId: command.workerId ?? '—',
    commandTypeShort: command.commandTypeShort ?? '',
    error: command.error ?? null,
    children,
  };
}

function toHttpNode(call: ExternalCallTreeLeaf): RequestTreeNode {
  return {
    id: `http:${call.id}`,
    kind: 'http',
    title: call.urlLabel,
    isUnassigned: false,
    hasCalls: true,
    isExternal: true,
    expanded: false,
    state: '—',
    severity: call.severity,
    statusLabel: call.statusLabel,
    durationLabel: `${call.durationMs} ms`,
    proxyLabel: call.proxyLabel,
    callId: call.id,
    url: call.url,
    httpMethod: call.httpMethod,
    logs: [],
    inputJson: '—',
    resultJson: '—',
    ...emptyCommandMeta(),
    error: call.error,
    children: [],
  };
}
